import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowsRightLeftIcon, ChevronRightIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { LockedAutocomplete, type AutocompleteOption } from "@/components/LockedAutocomplete";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { DialogCancelButton, hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  ANSPRECHPARTNER_TEAM_MAIL_DOKUMENTARTEN,
  ansprechpartnerDisplayName,
  getKontaktAnsprechpartner,
  updateKontaktAnsprechpartner,
  type KontaktAnsprechpartner,
} from "@/lib/kontakte";
import { patchOrderAnsprechpartner } from "@/lib/projectOverview";
import type { HeroContactLookup } from "@/lib/projects";
import { toast } from "@/lib/toast";
import { useRealtimeSync } from "@/lib/useRealtime";
import { AnsprechpartnerFormFields } from "@/pages/kontakte/AnsprechpartnerFormFields";
import {
  ansprechpartnerFieldsMatch,
  apFormFromKontakt,
  apPayloadFromForm,
  applyApFormUpdate,
  validateApForm,
  type ApForm,
} from "@/pages/kontakte/ansprechpartnerFormState";
import { contactSource } from "@/pages/projekte/sources";
import { PROJECT_OVERVIEW_OVERLAY_Z } from "@/projectOverview/projectOverviewLayers";

export function AnsprechpartnerContactCard({
  orderId,
  ansprechpartnerId,
  fallbackName,
  geschaeftspartnerId = null,
  heroCustomerId = null,
  canEdit,
  onSaved,
}: {
  orderId: number;
  ansprechpartnerId: number | null;
  fallbackName?: string | null;
  geschaeftspartnerId?: number | null;
  heroCustomerId?: number | null;
  canEdit: boolean;
  onSaved?: () => void;
}) {
  const navigate = useNavigate();
  const [contact, setContact] = useState<KontaktAnsprechpartner | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"view" | "edit" | "change">("view");
  const [nextContact, setNextContact] = useState<AutocompleteOption<HeroContactLookup> | null>(null);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);
  const [form, setForm] = useState<ApForm | null>(null);
  const [formBaseline, setFormBaseline] = useState<ApForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const requestId = useRef(0);
  const savedOverlay = useRef<{ row: KontaktAnsprechpartner; until: number } | null>(null);

  const reload = useCallback(
    async (options?: { silent?: boolean }) => {
      const token = ++requestId.current;
      if (ansprechpartnerId == null) {
        setContact(null);
        setError(null);
        setLoading(false);
        return;
      }
      if (!options?.silent) setLoading(true);
      try {
        const row = await getKontaktAnsprechpartner(ansprechpartnerId);
        if (token !== requestId.current) return;
        const overlay = savedOverlay.current;
        const keepSaved =
          overlay != null &&
          overlay.row.id === row.id &&
          Date.now() < overlay.until &&
          !ansprechpartnerFieldsMatch(overlay.row, row);
        if (overlay && !keepSaved && overlay.row.id === row.id) savedOverlay.current = null;
        setContact(keepSaved && overlay ? overlay.row : row);
        setError(null);
      } catch (err) {
        if (token !== requestId.current) return;
        if (!options?.silent) {
          setContact(null);
          setError(
            err instanceof ApiError
              ? err.message
              : "Ansprechpartner konnte nicht geladen werden.",
          );
        }
      } finally {
        if (token === requestId.current && !options?.silent) setLoading(false);
      }
    },
    [ansprechpartnerId],
  );

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    savedOverlay.current = null;
    setContact(null);
    setError(null);
    setOpen(false);
    setMode("view");
    setNextContact(null);
    setChangeError(null);
  }, [ansprechpartnerId]);

  useRealtimeSync({
    domains: ["kontakte"],
    enabled: ansprechpartnerId != null,
    paused: open && mode === "edit",
    onChange: () => void reload({ silent: true }),
  });

  const name = contactName(contact, fallbackName);
  const preview = previewLines(contact);
  const editable = Boolean(
    canEdit && contact && !contact.deleted && contact.geschaeftspartner_id != null,
  );
  const customerId = contact?.geschaeftspartner_id ?? geschaeftspartnerId ?? null;
  const customerRef = useMemo(() => {
    const gpId = geschaeftspartnerId ?? contact?.geschaeftspartner_id ?? null;
    if (gpId == null && heroCustomerId == null) return null;
    return { id: heroCustomerId, geschaeftspartner_id: gpId };
  }, [geschaeftspartnerId, heroCustomerId, contact?.geschaeftspartner_id]);
  const canChangeContact = Boolean(canEdit && customerRef);
  const contactLoad = useMemo(
    () => (customerRef ? contactSource(customerRef) : async () => []),
    [customerRef],
  );

  function closeDialog() {
    setOpen(false);
    setMode("view");
    setFormError(null);
    setChangeError(null);
  }

  function openView() {
    setMode("view");
    setFormError(null);
    setOpen(true);
  }

  function beginEdit() {
    if (!contact || contact.geschaeftspartner_id == null) return;
    const initial = apFormFromKontakt(contact);
    setForm(initial);
    setFormBaseline(initial);
    setFormError(null);
    setChangeError(null);
    setMode("edit");
    setOpen(true);
  }

  function beginChange() {
    setNextContact(
      ansprechpartnerId
        ? {
            key: String(ansprechpartnerId),
            label: name,
            value: { id: ansprechpartnerId, full_name: name },
          }
        : null,
    );
    setChangeError(null);
    setFormError(null);
    setMode("change");
    setOpen(true);
  }

  async function handleChangeContact() {
    const nextId = nextContact?.value?.id ?? null;
    if (!nextId) {
      setChangeError("Bitte einen Ansprechpartner auswählen.");
      return;
    }
    if (nextId === ansprechpartnerId) {
      setMode("view");
      setChangeError(null);
      return;
    }
    setChanging(true);
    setChangeError(null);
    try {
      await patchOrderAnsprechpartner(orderId, nextId);
      toast.success("Ansprechpartner für dieses Projekt geändert.");
      setOpen(false);
      setMode("view");
      onSaved?.();
    } catch (err) {
      setChangeError(
        err instanceof ApiError ? err.message : "Ansprechpartner konnte nicht geändert werden.",
      );
    } finally {
      setChanging(false);
    }
  }

  async function handleSave() {
    if (!contact || !form || contact.geschaeftspartner_id == null) return;
    const invalid = validateApForm(form);
    if (invalid) {
      setFormError(invalid);
      return;
    }
    setSaving(true);
    setFormError(null);
    const token = ++requestId.current;
    try {
      const saved = await updateKontaktAnsprechpartner(
        contact.id,
        apPayloadFromForm(form, contact.geschaeftspartner_id),
      );
      if (token !== requestId.current) return;
      savedOverlay.current = { row: saved, until: Date.now() + 8000 };
      setContact(saved);
      setMode("view");
      onSaved?.();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  const editing = mode === "edit" && form != null && formBaseline != null;
  const changingContact = mode === "change";
  const changeDirty =
    changingContact && (nextContact?.value?.id ?? null) !== (ansprechpartnerId ?? null);

  return (
    <div className="eh-po-contact-block">
      <div className="eh-po-contact-block-head">
        <p className="eh-po-info-panel-title">Ansprechpartner</p>
      </div>

      {ansprechpartnerId != null ? (
        <button type="button" className="eh-po-contact" onClick={openView} aria-haspopup="dialog">
          <ContactFace name={name} contact={contact} fallbackName={fallbackName} />
          <span className="eh-po-contact-copy">
            <span className="eh-po-contact-name">{name}</span>
            {preview.map((line) => (
              <span key={line} className="eh-po-contact-meta">
                {line}
              </span>
            ))}
          </span>
          <ChevronRightIcon className="eh-po-contact-chevron" aria-hidden />
        </button>
      ) : (
        <div className="eh-po-contact eh-po-contact--static">
          <ContactFace name={name} contact={contact} fallbackName={fallbackName} />
          <span className="eh-po-contact-copy">
            <span className="eh-po-contact-name">{name}</span>
          </span>
        </div>
      )}

      <Dialog
        open={open}
        onClose={closeDialog}
        zIndexClass={PROJECT_OVERVIEW_OVERLAY_Z}
        title={
          editing
            ? "Ansprechpartner bearbeiten"
            : changingContact
              ? "Ansprechpartner ändern"
              : name
        }
        description={
          editing
            ? contact?.firmennamen || undefined
            : changingContact
              ? "Anderen Ansprechpartner für dieses Projekt auswählen."
              : contact?.funktion?.trim() || contact?.firmennamen || undefined
        }
        size="lg"
        dirty={
          (editing && form != null && formBaseline != null && hasFormChanges(form, formBaseline)) ||
          changeDirty
        }
        footer={
          editing ? (
            <>
              <DialogCancelButton onClose={closeDialog} disabled={saving} />
              <Button type="button" onClick={() => void handleSave()} disabled={saving}>
                {saving ? "Speichern…" : "Speichern"}
              </Button>
            </>
          ) : changingContact ? (
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setMode("view");
                  setChangeError(null);
                }}
                disabled={changing}
              >
                Abbrechen
              </Button>
              <Button
                type="button"
                onClick={() => void handleChangeContact()}
                disabled={changing || !changeDirty}
                loading={changing}
              >
                Übernehmen
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="secondary" onClick={closeDialog}>
                Schließen
              </Button>
              {customerId != null ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    closeDialog();
                    navigate(`/kontakte/kunden/${customerId}?tab=kontakte`);
                  }}
                >
                  Beim Kunden öffnen
                </Button>
              ) : null}
              {canChangeContact ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={beginChange}
                  leftIcon={<ArrowsRightLeftIcon className="h-4 w-4" aria-hidden />}
                >
                  Ändern
                </Button>
              ) : null}
              {editable ? (
                <Button
                  type="button"
                  onClick={beginEdit}
                  leftIcon={<PencilSquareIcon className="h-4 w-4" aria-hidden />}
                >
                  Bearbeiten
                </Button>
              ) : null}
            </>
          )
        }
      >
        {editing && form ? (
          <>
            {formError ? (
              <Alert variant="error" title="Eingabe" className="mb-4">
                {formError}
              </Alert>
            ) : null}
            <AnsprechpartnerFormFields
              key={contact?.id ?? "edit"}
              form={form}
              onChange={(updater) =>
                setForm((current) => (current ? applyApFormUpdate(current, updater) : current))
              }
            />
          </>
        ) : changingContact ? (
          <div className="space-y-4">
            {changeError ? (
              <Alert variant="error" title="Ansprechpartner">
                {changeError}
              </Alert>
            ) : null}
            <LockedAutocomplete<HeroContactLookup>
              label="Ansprechpartner"
              required
              autoFocus
              placeholder="Ansprechpartner suchen …"
              value={nextContact}
              loadOptions={contactLoad}
              onChange={setNextContact}
              emptyText="Kein Ansprechpartner gefunden."
            />
          </div>
        ) : (
          <ContactDetails
            contact={contact}
            loading={loading}
            error={error}
            fallbackName={fallbackName}
          />
        )}
      </Dialog>
    </div>
  );
}

function ContactFace({
  name,
  contact,
  fallbackName,
}: {
  name: string;
  contact: KontaktAnsprechpartner | null;
  fallbackName?: string | null;
}) {
  return (
    <span className="eh-po-contact-avatar" aria-hidden>
      {initials(contact, fallbackName || name)}
    </span>
  );
}

function ContactDetails({
  contact,
  loading,
  error,
  fallbackName,
}: {
  contact: KontaktAnsprechpartner | null;
  loading: boolean;
  error: string | null;
  fallbackName?: string | null;
}) {
  if (loading && !contact) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error ? (
        <Alert variant="error" title="Kontakt">
          {error}
        </Alert>
      ) : null}
      {contact?.deleted ? (
        <Alert variant="warning" title="Gelöscht">
          Dieser Ansprechpartner ist als gelöscht markiert.
        </Alert>
      ) : null}
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
        <DetailRow label="Name" value={contactName(contact, fallbackName)} />
        <DetailRow label="Firma" value={contact?.firmennamen} />
        <DetailRow label="Funktion" value={contact?.funktion} />
        <DetailRow label="Telefon" value={contact?.telefonnummer} hrefKind="tel" />
        <DetailRow label="Mobil" value={contact?.mobiltelefon} hrefKind="tel" />
        <DetailRow label="E-Mail" value={contact?.mail} hrefKind="mail" />
        {contact?.team_mail?.trim() ? (
          <DetailRow label="Team-Mail" value={contact.team_mail} hrefKind="mail" />
        ) : null}
        {teamMailSummary(contact?.team_mail_dokumentarten) ? (
          <DetailRow
            label="Auch an die Team-Mail"
            value={teamMailSummary(contact?.team_mail_dokumentarten)}
            wide
          />
        ) : null}
      </dl>
    </div>
  );
}

function DetailRow({
  label,
  value,
  hrefKind,
  wide,
}: {
  label: string;
  value?: string | null;
  hrefKind?: "tel" | "mail";
  wide?: boolean;
}) {
  const text = (value || "").trim();
  return (
    <div className={wide ? "sm:col-span-2" : undefined}>
      <dt className="text-label-tertiary">{label}</dt>
      <dd className="mt-0.5 text-label">
        {text && hrefKind === "tel" ? (
          <a href={`tel:${text}`} className="tabular-nums hover:text-accent hover:underline">
            {text}
          </a>
        ) : text && hrefKind === "mail" ? (
          <a href={`mailto:${text}`} className="hover:text-accent hover:underline">
            {text}
          </a>
        ) : (
          text || "—"
        )}
      </dd>
    </div>
  );
}

function contactName(
  contact: KontaktAnsprechpartner | null,
  fallbackName?: string | null,
): string {
  if (contact) {
    const name = ansprechpartnerDisplayName(contact);
    if (name !== "—") return name;
  }
  return (fallbackName || "").trim() || "Ansprechpartner";
}

function previewLines(contact: KontaktAnsprechpartner | null): string[] {
  if (!contact) return [];
  const funktion = (contact.funktion || "").trim();
  const phone = (contact.mobiltelefon || contact.telefonnummer || "").trim();
  const mail = (contact.mail || "").trim();
  const reach = [phone, mail].filter(Boolean).join(" · ");
  return [funktion, reach].filter(Boolean);
}

function initials(contact: KontaktAnsprechpartner | null, fallback: string): string {
  const vorname = (contact?.vorname || "").trim();
  const nachname = (contact?.nachname || "").trim();
  const fromPerson = `${vorname[0] ?? ""}${nachname[0] ?? ""}`.toUpperCase();
  if (fromPerson) return fromPerson;
  const parts = fallback
    .split(/\s+/)
    .map((part) => part.trim())
    .filter((part) => part && part !== "—" && !/^(herr|frau|divers)$/i.test(part));
  const letters = parts
    .slice(0, 2)
    .map((part) => part[0] ?? "")
    .join("")
    .toUpperCase();
  return letters || "?";
}

function teamMailSummary(keys: string[] | null | undefined): string {
  const chosen = new Set(keys || []);
  return ANSPRECHPARTNER_TEAM_MAIL_DOKUMENTARTEN.filter((art) => chosen.has(art.key))
    .map((art) => art.label)
    .join(", ");
}
